import store from "../../store";
import { scaleLinear, min, forceSimulation, forceX, forceY, forceCollide, linkHorizontal, axisBottom, axisTop } from "d3";
import { deepCopy } from "../../services/helpers";
import { uniqBy } from "lodash";

export default class {
	constructor (artist) {
		this.artist = artist;
	}

	async get () {
    
		this.MIN_YEAR_LIMIT = 1900;
		this.MAX_YEAR_LIMIT = 2015;
		this.BASE_RADIUS = 4;

		this.EDGE_CASE_DUPLICATA = "duplicata";
		this.EDGE_CASE_SAME_LEVEL_OWN = "same_level_own";
		this.EDGE_CASE_SAME_LEVEL_SOURCE = "same_level_source";
    
		/*
    |--------------------------------------------------------------------------
    | Layout defs
    |--------------------------------------------------------------------------
    */

		this.width = 1332;
		this.height = 520;    
    
		this.row = {
			height: this.height/3
		};
    
		this.cover = {
			width: 180,
			margin: {
				left: 30,
				right: 0
			}
		};

		this.past = {
			width: 200,
			margin: {
				left: 70,
				right: 18
			}
		};

		/*
    |--------------------------------------------------------------------------
    | Levels
    |--------------------------------------------------------------------------
    */
    
		this.levels = [
			"source",
			"own",
			"target"
		];

		/*
    |--------------------------------------------------------------------------
    | Colors
    |--------------------------------------------------------------------------
    */
    
		this.colors = [
			"#4299e1",
			"#a0aec0",
			"#f6ad55",
			"#ffffff"
		];
    
		/*
    |--------------------------------------------------------------------------
    | Base data
    |--------------------------------------------------------------------------
    */
    
		let ownSongs = this.ownSongs();
		let ownSongIds = ownSongs.map(s => s.id);
    
		let sourceSongs = this.sourceSongs(ownSongIds);
    
		this.songs = {
			source: sourceSongs,
			own: ownSongs,
			target: this.targetSongs(ownSongIds),
			all: [],
			allWithDuplicates: [],
			duplicates: [],
			covers: {
				own: this.ownCoverSongs(sourceSongs),
				target: this.targetCoverSongs(ownSongs)
			}
		};
    
		/*
    |--------------------------------------------------------------------------
    | Scales
    |--------------------------------------------------------------------------
    */

		// For domain calculation only
		let allSongs = uniqBy(
			this.songs.own.concat(this.songs.source).concat(this.songs.target), 
			"id"
		);
    
		// Found the new year limit
		this.MIN_YEAR_LIMIT = this.findMinYearLimit(allSongs);
    
		// Generate the domain
		this.domain = [this.MIN_YEAR_LIMIT, this.MAX_YEAR_LIMIT];
    
		// Generate the X scale for the timeline
		this.x = scaleLinear()
			.domain(this.domain)
			.range([this.past.width + 30, this.width - this.cover.width - 10]);
      
		/*
    |--------------------------------------------------------------------------
    | Axis
    |--------------------------------------------------------------------------
    */

		// X Axis
		this.axis = {
			x: axisTop(this.x).ticks(6, "d")
		};
    
		/*
    |--------------------------------------------------------------------------
    | Enrich data
    |--------------------------------------------------------------------------
    */

		this.maxSongsPerRow = Math.max(
			this.songs.source.length, 
			this.songs.own.length, 
			this.songs.target.length, 
			this.songs.covers.own.length,
			this.songs.covers.target.length
		);

		this.songs.source = this.songs.source.map(s => {
			s.n_links = 0;
			s.complete_tree = false;
			s.level = 0;
			s.cover = false;
			s.radius = this.getRadius(s);
			return s;
		});
    
		this.songs.own = this.songs.own.map(s => {
			s.n_links = 0;
			s.complete_tree = true;
			s.level = 1;
			s.cover = false;
			s.radius = this.getRadius(s);
			return s;
		});
    
		this.songs.target = this.songs.target.map(s => {
			s.n_links = 0;
			s.complete_tree = s.n_children == 0;
			s.level = 2;
			s.cover = false;
			s.radius = this.getRadius(s);
			return s;
		});
    
		this.songs.covers.own = this.songs.covers.own.map(s => {
			s.level = 1;
			s.cover = true;
			s.radius = this.getRadius(s);
			return s;
		});
    
		this.songs.covers.target = this.songs.covers.target.map(s => {
			s.level = 2;
			s.cover = true; 
			s.radius = this.getRadius(s);
			return s;
		});
    
		/*
    |--------------------------------------------------------------------------
    | Calculate Completed Tree for Sources
    |--------------------------------------------------------------------------
    */

		this.songs.source.forEach(source => {
			this.songs.own.forEach(target => {
				if (source.id == target.parent_id && source.level <= target.level) {
					// Add complete_tree attribute for sources
					source.n_links++;
					if (source.n_links >= source.n_children && source.parent_id == "") {
						source.complete_tree = true;
						source.radius = this.getRadius(source);
					}
				}
			});
		});

		/*
    |--------------------------------------------------------------------------
    | Force Simulation
    |--------------------------------------------------------------------------
    */
    
		this.forceSimulation(this.songs["source"]);
		this.forceSimulation(this.songs["own"]);
		this.forceSimulation(this.songs["target"]);
		this.forceSimulation(this.songs["covers"]["own"], 0.1);
		this.forceSimulation(this.songs["covers"]["target"], 0.1);
    
		/*
    |--------------------------------------------------------------------------
    | Lookups
    |--------------------------------------------------------------------------
    */
    
		this.lookups = {
			source: {},
			own: {}
		};

		this.songs["source"].forEach(s => {
			this.lookups.source[s.id] = s;
		});
    
		this.songs["own"].forEach(s => {
			this.lookups.own[s.id] = s;
		});
    
		// All songs excluding covers
		this.songs.allWithDuplicates = this.songs.own.concat(this.songs.source).concat(this.songs.target);
    
		// Find all duplicates are remove them
		this.songs.all = [];
		const map = new Map();

		for (const item of this.songs.allWithDuplicates) {
			if (!map.has(item.id)) {
				map.set(item.id, true);
				this.songs.all.push(item);
			} else {
				this.songs.duplicates.push(item);
			}
		}
    
		/*
    |--------------------------------------------------------------------------
    | Links
    |--------------------------------------------------------------------------
    */

		this.links = [];
    
		// Timeline songs
		this.songs.allWithDuplicates.forEach(source => {
			this.songs.allWithDuplicates.forEach(target => {
				if (source.id == target.parent_id && source.level <= target.level) {
					this.links.push({
						source: source,
						target: target
					});
				}
			});
		});
    
		// Covers
		this.songs["covers"]["own"].forEach(cover => {
			this.links.push({
				source: this.lookups.source[cover.id],
				target: cover
			});
		});
    
		this.songs["covers"]["target"].forEach(cover => {
			this.links.push({
				source: this.lookups.own[cover.id],
				target: cover
			});
		});
    
		// Link diagonal
		this.diagonal = linkHorizontal()
			.x(d => d.x)
			.y(d => d.y);
      
		/*
    |--------------------------------------------------------------------------
    | Artists Lists
    |--------------------------------------------------------------------------
    */
    
		this.artists = {};
    
		this.songs.source.forEach(song => {
			["composer", "author", "interpret"].forEach(role => {
				song[`${role}_ids`].forEach(id => {
					this.addArtistData(id, 0, role);
				});
			});
		});
    
		this.songs.own.forEach(song => {
			["composer", "author", "interpret"].forEach(role => {
				song[`${role}_ids`].forEach(id => {
					this.addArtistData(id, 1, role);
				});
			});
		});
    
		this.songs.target.forEach(song => {
			["composer", "author", "interpret"].forEach(role => {
				song[`${role}_ids`].forEach(id => {
					this.addArtistData(id, 2, role);
				});
			});
		});
    
		this.songs.covers.own.forEach(song => {
			this.addArtistData(song.cover_artist_id, 1, "interpret");
		});
    
		this.songs.covers.target.forEach(song => {
			this.addArtistData(song.cover_artist_id, 2, "interpret");
		});
    
		this.artists = Object.values(this.artists);
    
		/*
    |--------------------------------------------------------------------------
    | Edge cases detection
    |--------------------------------------------------------------------------
    */

		this.edgeCases = [];
    
		this.detectEdgeCases();
    
		return this;
	}
  
	/**
   * Force simulation
   * 
   * @param Array songs
   * @return void
   */
	forceSimulation (songs, force = 0.3, nTicks = null) {
		let simulation = forceSimulation(songs)
			.force("x", forceX(d => this.xScale(d)).strength(force))
			.force("y", forceY(d => this.yScale(d)))
			.force("collide", forceCollide(d => d.radius))
			.stop();
      
		if (nTicks == null) {
			nTicks = Math.max(80, songs.length * 1.5);
		}

		for (let i = 0; i < nTicks; ++i) simulation.tick();
	}
  
	/**
   * Get radius
   * 
   * @param Object song
   * @return Number radius 
   */
	getRadius (song) {
		let radius = this.BASE_RADIUS;
		if (!song.cover) {
			radius += (song.n_children + song.cover_ids.length) * 0.15;
			if (!song.complete_tree) {
				radius += 0.6;
			}
		}
		return radius;
	}
  
	/**
   * x scale for the complete viz
   * 
   * @param Object d 
   */
	xScale (d) {
		if (d.cover) {
			return this.width - (this.cover.width / 2);
		}

		if (d.year < this.MIN_YEAR_LIMIT) {
			let domain = this.MIN_YEAR_LIMIT - 1500;
			return this.past.margin.left + (this.past.width - this.past.margin.left - this.past.margin.right) * (d.year - 1500) / domain;
		}

		return this.x(d.year);
	}
  
	/**
   * y scale for the complete viz
   * 
   * @param Object d 
   */
	yScale (d) {
		return d.level * this.row.height + this.row.height / 2;
	}
  
	/**
   * Find the min year limit
   * 
   * @param array songs 
   */
	findMinYearLimit (songs) {
		return min(
			songs.filter(s => s.year >= this.MIN_YEAR_LIMIT)
				.map(s => parseInt(s.year))
		);
	}
  
	/**
   * Get artist's target songs
   * 
   * @param array
   */
	sourceSongs (excluding) {
		let data = deepCopy(Object.values(store.state.songs)
			.filter(s => {
				return s.parent_id != "" && s.original_artist_ids.includes(this.artist.id);
			}))
			.map(s => store.state.songs[s.parent_id])
			.concat(Object.values(store.state.songs).filter(s => s.cover_ids.includes(this.artist.id))) // Add covered songs
			.filter(s => !excluding.includes(s.id) && s.year != "");
      
		return uniqBy(data, "id");
	}
  
	/**
   * Get artist's own songs
   * 
   * @return array
   */
	ownSongs () {       
		return deepCopy(Object.values(store.state.songs)
			.filter(s => {
				return s.year != "" && s.original_artist_ids.includes(this.artist.id);
			}));
	}
  
	/**
   * Get artist's own cover songs
   * 
   * @param array songs
   * @return array
   */
	ownCoverSongs (songs) {
    
		let allCovers = [];
		let covers = songs.filter(s => s.cover_ids.includes(this.artist.id));
    
		covers.forEach(cover => {
			let cloneCover = deepCopy(cover);
			cloneCover.cover_artist_id = this.artist.id;
			allCovers.push(cloneCover);
		});
    
		return allCovers;
	}
  
	/**
   * Get artist's target songs
   * 
   * @param array
   */
	targetSongs (excluding) {
		return deepCopy(Object.values(store.state.songs)
			.filter(s => {
				return s.parent_id != "" && 
          s.artist_id != "" && 
          store.state.songs[s.parent_id].original_artist_ids.includes(this.artist.id) &&
          !excluding.includes(s.id) &&
          s.year != "";
			}));
	}
  
	/**
   * Get artist's target cover songs
   * 
   * @param array songs
   * @return array
   */
	targetCoverSongs (songs) {
		let allCovers = [];
		let covers = songs.filter(s => s.cover_ids.length > 0);
    
		covers.forEach(cover => {
			cover.cover_ids.filter(id => id != this.artist.id).forEach(id => {
				let cloneCover = deepCopy(cover);
				cloneCover.cover_artist_id = id;
				allCovers.push(cloneCover);
			});
		});
    
		return allCovers;
	}

	/**
   * Add Artist data (create or edit)
   * 
   * @param {*} id 
   * @param {*} level 
   * @param {*} role 
   */
	addArtistData (id, level, role) {
		if (this.artists[id] === undefined) {
			this.artists[id] = {
				id: id,
				artist: store.state.artists[id],
				scores: [
					{
						composer: 0,
						author: 0,
						interpret: 0,
						total: 0
					},
					{
						composer: 0,
						author: 0,
						interpret: 0,
						total: 0
					},
					{
						composer: 0,
						author: 0,
						interpret: 0,
						total: 0
					}
				]
			};
		}

		this.artists[id].scores[level][role]++;
		this.artists[id].scores[level].total++;
	}
  
	detectEdgeCases () {
		// Duplicates
		this.songs.duplicates.forEach(song => {
			this.edgeCases.push({
				data: [song],
				type: this.EDGE_CASE_DUPLICATA
			});
		});
    
		// Same level 

		// Source
		this.songs.source.forEach(song => {
			let parent = this.lookups.source[song.parent_id];
			if (parent !== undefined) {
				this.edgeCases.push({
					data: [song, parent],
					type: this.EDGE_CASE_SAME_LEVEL_SOURCE
				});
			}
		});
    
		// Own
		this.songs.own.forEach(song => {
			let parent = this.lookups.own[song.parent_id];
			if (parent !== undefined) {
				this.edgeCases.push({
					data: [song, parent],
					type: this.EDGE_CASE_SAME_LEVEL_OWN
				});
			}
		});
	}
}
